import React, { ReactNode } from "react";
import exerciseScale from "../constants/exercise-scale";
import programDrillType from "../constants/program-drill-type";
import calculateSize from "calculate-size/lib/index";
import moment from "moment";
import mime from "mime-types";
import i18n from "i18next";
import { HelpArticle } from "../constants/help-articles";

export const t = (key: string, options?: any): string => String(i18n.t(key, options));

export interface Drill {
  program_drill_type: number;
  measures: Record<string, string>;
  rest_time?: string;
  note?: string;
  no_note_instructions?: string;
}

export function getDrillPrescription(drill: Drill, includeInstructions: boolean = true): string | null {
  if (drill.program_drill_type === programDrillType.EXERCISE) {
    const scales = [
      {
        id: exerciseScale.SETS,
        title: t("sets"),
      },
      {
        id: exerciseScale.REP,
        title: t("reps"),
      },
      {
        id: exerciseScale.WEIGHT,
        title: t("weight"),
      },
      {
        id: exerciseScale.DISTANCE,
        title: t("distance"),
      },
      {
        id: exerciseScale.DURATION,
        title: t("duration"),
      },
    ];
    const prescription: string[] = [];
    const measures = drill.measures;
    for (const scale of scales) {
      const key = scale.id.toString();
      if (key in measures && measures[key] !== "0" && measures[key] !== "0.0") {
        prescription.push(`${scale.title} ${measures[key]}`);
      }
    }
    if (
      drill.rest_time &&
      drill.rest_time !== "0" &&
      drill.rest_time !== "0.0"
    ) {
      const localeRestStr = t("rest");
      prescription.push(`${localeRestStr}: ${drill.rest_time}`);
    }
    let note = "";
    if (drill.note) {
      note = note + drill.note + ". ";
    }
    if (includeInstructions) {
      if (drill.no_note_instructions) {
        note = note + drill.no_note_instructions;
      }
    }
    if (note) {
      const localeNoteStr = t("note");
      prescription.push(`${localeNoteStr} ${note}`);
    }
    if (prescription.length > 0) {
      return prescription.join(" \u00B7 ");
    }
  } else if (
    drill.program_drill_type === programDrillType.START_SUPERSET ||
    drill.program_drill_type === programDrillType.START_CIRCUIT
  ) {
    const key = exerciseScale.SETS.toString();
    if (drill.measures[key] !== "0") {
      if (drill.program_drill_type === programDrillType.START_SUPERSET) {
        const localeSetsStr = t("sets");
        return `${localeSetsStr} ${drill.measures[key]}`;
      } else {
        const localeRoundsStr = t("rounds");
        return `${localeRoundsStr}: ${drill.measures[key]}`;
      }
    }
    return null;
  }
  return null;
}

interface CalculateTextHeightOptions {
  font?: string;
  fontSize?: string;
}

export function calculateTextHeight(
  text: string,
  width: number,
  options?: CalculateTextHeightOptions
): number {
  text = text ? text : "";
  let height = 0;
  const split = text.split("\n");
  let font = "Roboto, sans-serif";
  let fontSize = "14px";
  if (options) {
    if (options.font) {
      font = options.font;
    }
    if (options.fontSize) {
      fontSize = options.fontSize;
    }
  }
  for (let i = 0; i < split.length; i++) {
    const size = calculateSize(split[i].trim() === "" ? "dummy" : split[i], {
      font: font,
      fontSize: fontSize,
      width: `${width}px`,
    });
    height = height + size.height;
  }
  return height;
}

export function htmlLines(text: string): ReactNode[] {
  const split = text.split("\n");
  const lines: ReactNode[] = [];
  for (let i = 0; i < split.length; i++) {
    if (i > 0) {
      lines.push(<br key={`b-${i}`} />);
    }
    lines.push(<span key={`l-${i}`}>{split[i]}</span>);
  }
  return lines;
}

export function buildInviteEmailForClient(params: Record<string, any>): string {
  params["newline"] = "%0D%0A";
  return t("clientWelcomeEmail", params);
}

export function buildInviteEmailForTrainer(params: Record<string, any>): string {
  params["newline"] = "%0D%0A";
  return t("trainerWelcomeEmail", params);
}

export function getPhotoUrl(photoId: number): string {
  let photoUrl = `/users_photos/${photoId}`;
  if (process.env.NODE_ENV === "stage") {
    photoUrl = (process.env.ROOT_URL ?? "") + photoUrl;
  }
  return photoUrl;
}

export function getAbsoluteUrl(url: string): string {
  let absoluteUrl = url;
  if (process.env.NODE_ENV === "stage") {
    absoluteUrl = (process.env.ROOT_URL ?? "") + absoluteUrl;
  }
  return absoluteUrl;
}

type MediaLink = { image?: string; video?: string; text?: string };

export function extractMediaLink(content: string): MediaLink {
  const mimeType = mime.lookup(content);
  if (mimeType) {
    const resourceType = mimeType.substr(0, 5);
    switch (resourceType) {
      case "image":
        return {
          image: content,
        };
      case "video":
        return {
          video: content,
        };
      default:
        return {
          text: content,
        };
    }
  }
  return {
    text: content,
  };
}

export interface Message {
  id: string;
  message: string;
  date_time: string;
  user_id: string;
  first_name: string;
  photo_id?: number;
  mime_type?: string;
  files?: any;
  is_checkin?: boolean;
  did_checkin?: boolean;
  featured_link?: string;
}

export function formatMessageObject(message: Message) {
  const { text, image, video } = extractMediaLink(message.message);
  return {
    _id: message.id,
    text,
    image,
    video,
    createdAt: moment.utc(message.date_time).local(),
    user: {
      _id: message.user_id,
      name: message.first_name,
      avatar: message.photo_id
        ? getPhotoUrl(message.photo_id)
        : "",
    },
    mimeType: message.mime_type,
    message: message.message,
    files: message.files,
    isCheckin: message.is_checkin,
    didCheckin: message.did_checkin,
    featuredLink: message.featured_link,
  };
}

export function hasSurrogateCharacters(str: string): boolean {
  const SURROGATE_REGEX = /[\ud800-\udbff][\udc00-\udfff]/g;
  return SURROGATE_REGEX.test(str);
}

export const getLocale = (): string => {
  return i18n.resolvedLanguage || "en";
};

export function convertNumberToLocaleString(n: number): string {
  const locale =
    (navigator.languages && navigator.languages.length)
      ? navigator.languages[0]
      : navigator.language;
  return new Intl.NumberFormat(locale).format(n);
}

export function convertLocaleStringToNumber(s: string): number {
  const locale =
    (navigator.languages && navigator.languages.length)
      ? navigator.languages[0]
      : navigator.language;
  const thousandSeparator = Intl.NumberFormat(locale).formatToParts(11111)[1].value;
  const decimalSeparator = Intl.NumberFormat(locale).formatToParts(1.1)[1].value;

  return parseFloat(
    s
      .replace(new RegExp('\\' + thousandSeparator, 'g'), '')
      .replace(new RegExp('\\' + decimalSeparator), '.')
  );
}

export function getHelpArticleURL(ha: HelpArticle): string {
  const lng = getLocale();

  if (lng === 'es') {
    if (ha === "APP_LINKS_COLLECTION") {
      return 'https://help.trainerfu.com/es/collections/3850612-enlaces-de-aplicaciones';
    } else if (ha === "AUTOMATED_PARQ_WAIVER") {
      return 'https://help.trainerfu.com/es/collections/3850593-automatizacion-par-q-waiver-onboarding-recordatorio-contenido';
    } else if (ha === "HABIT_COACHING_COLLECTION") {
      return 'https://help.trainerfu.com/es/collections/3848111-coaching-de-habitos';
    } else if (ha === "SEND_CONTENT_USING_AUTORESPONDER") {
      return 'https://help.trainerfu.com/es/articles/6974219-como-entregar-contenido-valioso-a-sus-clientes-de-forma-automatica-utilizando-autoresponder';
    } else if (ha === "SET_MFP_INTEGRATION") {
      return 'https://help.trainerfu.com/es/articles/6974199-como-ver-el-registro-de-comidas-de-myfitnesspal-de-su-cliente-dentro-de-su-aplicacion-de-entrenador';
    } else if (ha === "TRAINING_PACKAGE_TUTORIAL") {
      return 'https://help.trainerfu.com/es/collections/3848365-vender-paquetes-y-cobrar-pagos';
    } else {
      return 'https://help.trainerfu.com/es/';
    }
  } else {
    if (ha === "APP_LINKS_COLLECTION") {
      return 'https://help.trainerfu.com/en/collections/3850612-app-links';
    } else if (ha === "AUTOMATED_PARQ_WAIVER") {
      return 'https://help.trainerfu.com/en/collections/3850593-automation-par-q-waiver-onboarding-reminder-content';
    } else if (ha === "HABIT_COACHING_COLLECTION") {
      return 'https://help.trainerfu.com/en/collections/3848111-habit-coaching';
    } else if (ha === "SEND_CONTENT_USING_AUTORESPONDER") {
      return 'https://help.trainerfu.com/en/articles/6974219-how-to-deliver-valuable-content-to-your-clients-automatically-using-autoresponder';
    } else if (ha === "SET_MFP_INTEGRATION") {
      return 'https://help.trainerfu.com/en/articles/6974199-how-to-see-your-client-s-myfitnesspal-meal-log-inside-your-trainer-app';
    } else if (ha === "TRAINING_PACKAGE_TUTORIAL") {
      return 'https://help.trainerfu.com/en/collections/3848365-sell-packages-collect-payments';
    } else {
      return 'https://help.trainerfu.com/en/';
    }
  }
}